import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import {
  Check,
  X,
  Loader2,
  AlertCircle,
  ArrowRight,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import apiService from "../../services/actions";

const formatCurrency = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(Number(val))) {
    return "₹0.00";
  }
  const num = Number(val);
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  } catch {
    return String(dateStr);
  }
};

const getCurrentApproverCode = (user) => {
  if (user?.code) return user.code;
  if (user?.userCode) return user.userCode;
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj") || "null");
    if (loginObj?.userSession?.userCode) return loginObj.userSession.userCode;
    if (loginObj?.userCode) return loginObj.userCode;
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "null");
    if (userDetails?.userSession?.userCode) return userDetails.userSession.userCode;
  } catch (e) {
    console.error("Error reading approver code:", e);
  }
  return localStorage.getItem("userCode") || localStorage.getItem("access_name") || "FINANCE001";
};

export default function RoutedLabOrdersList({ user }) {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });

  const [approveModal, setApproveModal] = useState({
    open: false,
    item: null,
    remarks: "",
  });
  const [rejectModal, setRejectModal] = useState({
    open: false,
    item: null,
    remarks: "",
  });
  const [actionBusy, setActionBusy] = useState(false);

  const fetchList = useCallback(
    async (pageNo = 0) => {
      setLoading(true);
      try {
        const response = await apiService.getFinanceLabRoutingApprovals({
          status: "PENDING",
          page: pageNo,
          size: pagination.size || 20,
        });
        const data = response?.data;
        if (data?.success || response?.status === 200) {
          const list = Array.isArray(data?.requests)
            ? data.requests
            : Array.isArray(data?.data)
            ? data.data
            : [];
          setRequests(list);
          setPagination({
            page: data?.page !== undefined ? data.page : pageNo,
            size: data?.size || pagination.size || 20,
            totalElements: data?.totalElements || list.length,
            totalPages: data?.totalPages || (list.length > 0 ? 1 : 0),
          });
        } else {
          setRequests([]);
          toast.error(data?.message || "Failed to fetch routed lab orders");
        }
      } catch (err) {
        console.error("Error fetching finance lab routing approvals:", err);
        setRequests([]);
        toast.error(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load routed lab orders"
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.size]
  );

  useEffect(() => {
    fetchList(0);
  }, [fetchList]);

  const handlePageChange = (newPage) => {
    fetchList(newPage);
  };

  const handleApproveConfirm = async () => {
    if (!approveModal.item) return;
    const item = approveModal.item;
    const routeRef = item.routeReference || item.transactionId || item.originalOrderId;
    if (!routeRef) {
      toast.error("Route reference / Transaction ID missing for this order");
      return;
    }

    setActionBusy(true);
    try {
      const payload = {
        approverCode: getCurrentApproverCode(user),
        remarks: approveModal.remarks?.trim() || "",
      };
      const response = await apiService.approveFinanceLabRouting(routeRef, payload);
      const data = response?.data;
      if (data?.success || response?.status === 200) {
        toast.success(
          data?.message || "Replacement order was created and vendor booking was queued."
        );
        setApproveModal({ open: false, item: null, remarks: "" });
        fetchList(pagination.page);
      } else {
        toast.error(data?.message || "Failed to approve routed lab order");
      }
    } catch (err) {
      console.error("Error approving routed lab order:", err);
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to approve routed lab order"
      );
    } finally {
      setActionBusy(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal.item) return;
    const item = rejectModal.item;
    const routeRef = item.routeReference || item.transactionId || item.originalOrderId;
    if (!routeRef) {
      toast.error("Route reference / Transaction ID missing for this order");
      return;
    }

    setActionBusy(true);
    try {
      const payload = {
        approverCode: getCurrentApproverCode(user),
        remarks: rejectModal.remarks?.trim() || "",
      };
      const response = await apiService.rejectFinanceLabRouting(routeRef, payload);
      const data = response?.data;
      if (data?.success || response?.status === 200) {
        toast.success(data?.message || "Finance rejected the routing request.");
        setRejectModal({ open: false, item: null, remarks: "" });
        fetchList(pagination.page);
      } else {
        toast.error(data?.message || "Failed to reject routed lab order");
      }
    } catch (err) {
      console.error("Error rejecting routed lab order:", err);
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to reject routed lab order"
      );
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Table */}
      <div className="overflow-x-auto max-h-[calc(100vh-320px)] min-h-[350px] rounded-xl border border-slate-200 shadow-xs bg-white">
        <table className="w-full text-left text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 shadow-xs">
            <tr>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 whitespace-nowrap">Order ID</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 whitespace-nowrap">Reference / Txn</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Original Lab</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Routed Lab</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 text-right whitespace-nowrap">Orig. Cost</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 text-right whitespace-nowrap">Routed Cost</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 text-right whitespace-nowrap">Cost Difference</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Route Reason</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 border-b border-slate-200 whitespace-nowrap">Requested Date</th>
              <th className="sticky top-0 z-10 bg-slate-50 px-3.5 py-3 text-right border-b border-slate-200 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading && (
              <tr>
                <td colSpan={10} className="py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    <span className="text-xs font-medium">Loading routed lab orders...</span>
                  </div>
                </td>
              </tr>
            )}

            {!loading && requests.length === 0 && (
              <tr>
                <td colSpan={10} className="py-16 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                    <AlertCircle className="h-7 w-7 text-slate-300" />
                    <span className="text-sm font-semibold text-slate-600">No routed lab orders found</span>
                    <span className="text-xs text-slate-400">
                      There are currently no pending routed lab orders for finance approval.
                    </span>
                  </div>
                </td>
              </tr>
            )}

            {!loading &&
              requests.map((row, idx) => {
                const addCost = Number(row.additionalPartnerCost || 0);
                const isSavings = addCost < 0;
                const isExtra = addCost > 0;
                const txnId = row.transactionId || row.routeReference || "—";

                return (
                  <tr key={row.originalOrderId || row.transactionId || idx} className="hover:bg-slate-50/70 transition-colors">
                    {/* Order ID */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className="font-mono text-xs font-semibold text-slate-900">
                        {row.originalOrderId || "—"}
                      </span>
                    </td>

                    {/* Reference / Transaction ID */}
                    <td className="px-3.5 py-3 max-w-[130px]">
                      <span className="font-mono text-xs text-slate-600 break-all block" title={txnId}>
                        {txnId}
                      </span>
                    </td>

                    {/* Original Lab */}
                    <td className="px-3.5 py-3 max-w-[160px]">
                      <div className="flex flex-col items-start gap-0.5">
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800 break-words whitespace-normal text-left"
                          title={row.originalLabCode ? `Code: ${row.originalLabCode}` : undefined}
                        >
                          {row.originalLabName || row.originalLabCode || "—"}
                        </span>
                        {row.originalLabName && row.originalLabCode && row.originalLabName !== row.originalLabCode && (
                          <span className="text-[10px] font-mono text-slate-400 pl-0.5">
                            {row.originalLabCode}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Routed Lab */}
                    <td className="px-3.5 py-3 max-w-[160px]">
                      <div className="flex flex-col items-start gap-0.5">
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 border border-indigo-100 break-words whitespace-normal text-left"
                          title={row.routedLabCode ? `Code: ${row.routedLabCode}` : undefined}
                        >
                          {row.routedLabName || row.routedLabCode || "—"}
                        </span>
                        {row.routedLabName && row.routedLabCode && row.routedLabName !== row.routedLabCode && (
                          <span className="text-[10px] font-mono text-indigo-400 pl-0.5">
                            {row.routedLabCode}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Original Partner Cost */}
                    <td className="px-3.5 py-3 text-right whitespace-nowrap">
                      <span className="text-xs font-medium text-slate-700">
                        {formatCurrency(row.originalPartnerCost)}
                      </span>
                    </td>

                    {/* Routed Partner Cost */}
                    <td className="px-3.5 py-3 text-right whitespace-nowrap">
                      <span className="text-xs font-medium text-slate-700">
                        {formatCurrency(row.routedPartnerCost)}
                      </span>
                    </td>

                    {/* Cost Difference */}
                    <td className="px-3.5 py-3 text-right whitespace-nowrap">
                      {isSavings ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200">
                          <TrendingDown className="h-3 w-3" />
                          -{formatCurrency(Math.abs(addCost))} (Savings)
                        </span>
                      ) : isExtra ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700 border border-rose-200">
                          <TrendingUp className="h-3 w-3" />
                          +{formatCurrency(addCost)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          ₹0.00
                        </span>
                      )}
                    </td>

                    {/* Route Reason */}
                    <td className="px-3.5 py-3 max-w-[200px]">
                      <span
                        className="block text-xs text-slate-600 break-words whitespace-normal"
                        title={row.routeReason || ""}
                      >
                        {row.routeReason || "—"}
                      </span>
                    </td>

                    {/* Requested Date */}
                    <td className="px-3.5 py-3 whitespace-nowrap">
                      <span className="text-xs text-slate-500">
                        {formatDateTime(row.requestedDate)}
                      </span>
                    </td>

                    {/* Actions: Approve and Reject only */}
                    <td className="px-3.5 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() =>
                            setApproveModal({
                              open: true,
                              item: row,
                              remarks: "",
                            })
                          }
                          title="Approve Routed Lab Order"
                          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-600 shadow-2xs hover:bg-emerald-50 hover:border-emerald-300 transition"
                        >
                          <Check className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setRejectModal({
                              open: true,
                              item: row,
                              remarks: "",
                            })
                          }
                          title="Reject Routed Lab Order"
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-600 shadow-2xs hover:bg-rose-50 hover:border-rose-300 transition"
                        >
                          <X className="h-3.5 w-3.5" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>
              Page {pagination.page + 1} of {pagination.totalPages} ({pagination.totalElements} total requests)
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 0}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages - 1}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {approveModal.open && approveModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                <Check className="h-4 w-4" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Confirm Order Approval</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Are you sure you want to approve this routed lab order? A replacement order will be created and vendor booking queued.
            </p>

            <div className="rounded-xl bg-slate-50 p-3 text-xs space-y-2 border border-slate-200 mb-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Original Order ID:</span>
                <span className="font-mono font-semibold text-slate-800">{approveModal.item.originalOrderId}</span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-slate-500 shrink-0">Lab Routing:</span>
                <div className="text-right">
                  <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5 flex-wrap justify-end">
                    <span title={approveModal.item.originalLabCode ? `Code: ${approveModal.item.originalLabCode}` : undefined}>
                      {approveModal.item.originalLabName || approveModal.item.originalLabCode || "—"}
                    </span>
                    <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                    <span
                      className="text-indigo-600"
                      title={approveModal.item.routedLabCode ? `Code: ${approveModal.item.routedLabCode}` : undefined}
                    >
                      {approveModal.item.routedLabName || approveModal.item.routedLabCode || "—"}
                    </span>
                  </span>
                  {(approveModal.item.originalLabCode || approveModal.item.routedLabCode) && (
                    <div className="text-[10px] font-mono text-slate-400">
                      ({approveModal.item.originalLabCode || "—"} &rarr; {approveModal.item.routedLabCode || "—"})
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Original Cost:</span>
                <span className="font-semibold text-slate-800">{formatCurrency(approveModal.item.originalPartnerCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Routed Cost:</span>
                <span className="font-semibold text-slate-800">{formatCurrency(approveModal.item.routedPartnerCost)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-slate-200">
                <span className="text-slate-500 font-medium">Cost Difference:</span>
                <span className={`font-bold ${Number(approveModal.item.additionalPartnerCost) < 0 ? "text-emerald-600" : "text-slate-900"}`}>
                  {formatCurrency(approveModal.item.additionalPartnerCost)}
                </span>
              </div>
              {approveModal.item.routeReason && (
                <div className="pt-1 text-slate-600 italic">
                  &ldquo;{approveModal.item.routeReason}&rdquo;
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Approval Remarks:
              </label>
              <textarea
                rows={2}
                value={approveModal.remarks}
                onChange={(e) => setApproveModal((prev) => ({ ...prev, remarks: e.target.value }))}
                placeholder="Enter approval remarks..."
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => setApproveModal({ open: false, item: null })}
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={handleApproveConfirm}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 transition shadow-sm"
              >
                {actionBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {actionBusy ? "Approving..." : "Confirm Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {rejectModal.open && rejectModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                <X className="h-4 w-4" />
              </div>
              <h3 className="text-base font-bold text-slate-900">Reject Routed Lab Order</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Finance rejection will discard this routing request without changing the existing order.
            </p>

            <div className="rounded-xl bg-slate-50 p-3 text-xs space-y-2 border border-slate-200 mb-3">
              <div className="flex justify-between">
                <span className="text-slate-500">Order ID:</span>
                <span className="font-mono font-semibold text-slate-800">{rejectModal.item.originalOrderId}</span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-slate-500 shrink-0">Lab Route:</span>
                <div className="text-right">
                  <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5 flex-wrap justify-end">
                    <span title={rejectModal.item.originalLabCode ? `Code: ${rejectModal.item.originalLabCode}` : undefined}>
                      {rejectModal.item.originalLabName || rejectModal.item.originalLabCode || "—"}
                    </span>
                    <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                    <span
                      className="text-rose-600"
                      title={rejectModal.item.routedLabCode ? `Code: ${rejectModal.item.routedLabCode}` : undefined}
                    >
                      {rejectModal.item.routedLabName || rejectModal.item.routedLabCode || "—"}
                    </span>
                  </span>
                  {(rejectModal.item.originalLabCode || rejectModal.item.routedLabCode) && (
                    <div className="text-[10px] font-mono text-slate-400">
                      ({rejectModal.item.originalLabCode || "—"} &rarr; {rejectModal.item.routedLabCode || "—"})
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Rejection Remarks (Optional):
              </label>
              <textarea
                rows={3}
                value={rejectModal.remarks}
                onChange={(e) => setRejectModal((prev) => ({ ...prev, remarks: e.target.value }))}
                placeholder="Enter reason for rejecting this lab routing request..."
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-50"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => setRejectModal({ open: false, item: null, remarks: "" })}
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={handleRejectConfirm}
                className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 transition shadow-sm"
              >
                {actionBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                {actionBusy ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

RoutedLabOrdersList.propTypes = {
  user: PropTypes.object,
};
